import { MODAL_OPEN_CLOSE_EVENT } from '@interhyp/api-tracking';
import { Close } from '@interhyp/platform-icons';
import classnames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useMeasure } from 'react-use';
import { ModalProvider } from "./provider/ModalProvider.js";
import { createIntervalPromise } from "../../helper/functions/createIntervalPromise.js";
import { useMergedRef } from "../../helper/hooks/useMergedRef.js";
import { Backdrop } from "../backdrop/Backdrop.js";
import { IconButton } from "../iconButton/IconButton.js";
import { Portal } from "../portal/Portal.js";
import { Progress } from "../progress/Progress.js";
import { Typography } from "../typography/Typography.js";
import styles from "./Modal.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const usePrevious = value => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};
const useBackdropRef = ref => {
  const internalRef = useRef(null);
  return ref ?? internalRef;
};
const MODAL_CONTENT_ID = 'modal-content-id';
export const Modal = ({
  overline,
  title,
  children,
  className,
  closeable,
  onOpen,
  onClose,
  progress,
  close,
  onEsc,
  closeOnEsc,
  onOverlayClick,
  closeOnOverlayClick,
  /**
   * @deprecated this prop is now ignored because the modal will automatically focus the first element in its content
   */
  initialFocusRef,
  open = true,
  id,
  backdropRef,
  customProgress,
  noClip = false,
  role,
  size = 'M',
  'data-testid': dataTestId,
  refDocument = typeof window !== 'undefined' ? window.document : undefined,
  disableFocusTrap = false,
  trackingId
}) => {
  const modalRef = useRef(null);
  const previousOpen = usePrevious(open);
  const [scrollableContentRefDesktop, measureRectDesktop] = useMeasure();
  const [scrollableContentRefMobile, measureRectMobile] = useMeasure();
  const ref = useBackdropRef(backdropRef);
  const contentRef = useRef(null);
  const mergedContentRefDesktop = useMergedRef(ref, scrollableContentRefDesktop);
  const mergedContentRefMobile = useMergedRef(contentRef, scrollableContentRefMobile);
  const isContentScrollable = useMemo(() => {
    if (Math.round(measureRectMobile.height) < Math.round(contentRef.current?.scrollHeight ?? 0) || Math.round(measureRectDesktop.height) < Math.round(ref.current?.scrollHeight ?? 0)) {
      return true;
    }
  }, [measureRectDesktop.height, measureRectMobile.height, ref]);

  /**
   * calls onOpen
   *
   * runs whenever
   * - `onOpen` is present
   * - `open` changed to true
   */
  useEffect(() => {
    if (onOpen && open && previousOpen !== open) {
      onOpen();
    }
    if (open && previousOpen !== open) {
      MODAL_OPEN_CLOSE_EVENT.track({
        trackingId: trackingId ?? '',
        state: 'OPENED'
      });
    }
  }, [onOpen, open, previousOpen, trackingId]);

  /**
   * calls onClose
   *
   * runs whenever
   * - `onClose` is present
   * - `open` changed to false
   */
  useEffect(() => {
    if (onClose && !open && previousOpen !== open) {
      onClose();
    }
    if (!open && previousOpen !== open) {
      MODAL_OPEN_CLOSE_EVENT.track({
        trackingId: trackingId ?? '',
        state: 'CLOSED'
      });
    }
  }, [onClose, open, previousOpen, trackingId]);

  /**
   * adds an eventListener for keydown when open, watching for `Escape`
   *
   * runs whenever:
   * - `open` is true
   * - `onEsc` or `close` exists
   */
  useEffect(() => {
    if (open && (onEsc || close)) {
      const escapeListener = event => {
        if (event.key === 'Escape') {
          if (onEsc) {
            onEsc();
          }
          if (closeable && closeOnEsc && close) {
            close();
          }
        }
      };
      refDocument?.addEventListener('keydown', escapeListener);
      return () => {
        refDocument?.removeEventListener('keydown', escapeListener);
      };
    }
  }, [closeOnEsc, close, closeable, open, onEsc, refDocument]);
  if (!open) {
    return null;
  }
  const proxyOnOverlayClick = ({
    clientX,
    clientY,
    target
  }) => {
    const hasOnOverlayClick = !!onOverlayClick;
    const canClose = closeable && close && closeOnOverlayClick;
    if (hasOnOverlayClick || canClose) {
      // initialized as 0 to enable testing `onOverlayClick` - else both values are undefined
      const {
        x = 0,
        y = 0,
        width = 0,
        height = 0
      } = modalRef.current?.getBoundingClientRect() ?? {};
      const isXOutside = clientX < x || clientX > x + width;
      const isYOutside = clientY < y || clientY > y + height;
      const clickedScrollbar = clientX > target.clientWidth;
      const clickedOverlayDirectly = target === ref.current;
      const isOutside = isXOutside || isYOutside;
      if (!isOutside || clickedScrollbar || !clickedOverlayDirectly) {
        return;
      }
      if (hasOnOverlayClick) {
        onOverlayClick();
      }
      if (canClose) {
        close();
      }
    }
  };
  const portalClasses = classnames(styles.modal, className);
  const headerClasses = classnames(styles.header, {
    [styles.closeable]: closeable
  });
  const hasProgress = typeof progress === 'number';
  return /*#__PURE__*/_jsx(Portal, {
    id: id,
    className: portalClasses,
    refDocument: refDocument,
    children: /*#__PURE__*/_jsx(Backdrop, {
      onMouseDown: proxyOnOverlayClick,
      className: styles.backdrop,
      "data-testid": `${dataTestId}-backdrop`,
      children: /*#__PURE__*/_jsx(RemoveScroll, {
        className: styles.scrollLock,
        children: /*#__PURE__*/_jsx(ModalProvider, {
          children: /*#__PURE__*/_jsx("div", {
            ref: mergedContentRefDesktop,
            className: styles.portalContainer,
            children: /*#__PURE__*/_jsx(FocusTrap, {
              active: disableFocusTrap ? false : undefined,
              focusTrapOptions: {
                fallbackFocus: `#${MODAL_CONTENT_ID}`,
                allowOutsideClick: true,
                checkCanFocusTrap: a => createIntervalPromise(() => a.some(elm => elm.id === MODAL_CONTENT_ID))
              },
              children: /*#__PURE__*/_jsxs("article", {
                id: MODAL_CONTENT_ID,
                className: classnames(styles.body, {
                  [styles.noClip]: noClip,
                  [styles.footerShadow]: isContentScrollable
                }, styles[size]),
                ref: modalRef,
                role: role,
                "data-testid": dataTestId,
                tabIndex: -1,
                children: [/*#__PURE__*/_jsxs("header", {
                  className: headerClasses,
                  children: [overline && /*#__PURE__*/_jsx(Typography, {
                    className: styles.overline,
                    margin: false,
                    children: overline
                  }), title && /*#__PURE__*/_jsx(Typography, {
                    variant: size === 'S' ? 'h3' : 'h2',
                    id: `${id}-label`,
                    margin: false,
                    children: title
                  }), hasProgress && /*#__PURE__*/_jsx(Progress, {
                    value: progress,
                    className: styles.progress
                  }), customProgress]
                }), /*#__PURE__*/_jsx("div", {
                  className: styles.scrollingContent,
                  ref: mergedContentRefMobile,
                  children: children
                }), closeable && /*#__PURE__*/_jsx(IconButton, {
                  className: styles.closeButton,
                  icon: Close,
                  onClick: close,
                  toolbar: true,
                  tertiary: true
                })]
              })
            })
          })
        })
      })
    })
  });
};